import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/product/ProductCard';
import { Heart, Share2, Star, ExternalLink, ChevronLeft, ChevronRight, Shield, Truck, RefreshCw } from 'lucide-react';
import { wishlistAPI } from '../api';
import { getMarketplaceLabel } from '../utils/marketplace';
import toast from 'react-hot-toast';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { isLoggedIn } = useAuth();
  const [selectedImg, setSelectedImg] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [wishlisted, setWishlisted] = useState(false);
  const [activeTab, setActiveTab] = useState('description');

  const { data, isLoading } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => productAPI.getOne(slug).then(r => r.data),
  });

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '5rem' }}>
      <div className="spinner" />
    </div>
  );

  if (!data?.product) return (
    <div style={{ textAlign: 'center', padding: '5rem' }}>
      <h2>Product not found</h2>
      <Link to="/products" className="btn btn-primary" style={{ marginTop: 16 }}>Browse Products</Link>
    </div>
  );

  const { product, reviews, related } = data;
  const images = product.images?.length ? product.images : ['https://via.placeholder.com/500x600?text=No+Image'];
  const finalPrice = selectedVariant?.price || product.price;
  const discountPct = product.discount_percent;
  const marketplace = getMarketplaceLabel(product.external_url);

  const handleBuy = () => {
    if (!product.external_url) return;
    window.open(product.external_url, '_blank', 'noopener,noreferrer');
  };

  const handleWishlist = async () => {
    if (!isLoggedIn) { toast.error('Please login first'); return; }
    await wishlistAPI.toggle({ product_id: product.id });
    setWishlisted(!wishlisted);
    toast.success(wishlisted ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const ratingDistribution = [5, 4, 3, 2, 1].map(r => ({
    star: r,
    count: reviews.filter(rv => rv.rating === r).length,
    pct: reviews.length ? Math.round((reviews.filter(rv => rv.rating === r).length / reviews.length) * 100) : 0,
  }));

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Link to="/" style={{ color: '#9ca3af' }}>Home</Link> /
        <Link to="/products" style={{ color: '#9ca3af' }}>Products</Link> /
        <span style={{ color: '#374151' }}>{product.name}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginBottom: 40 }}>
        {/* Image gallery */}
        <div>
          <div style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', border: '1px solid #f0f0f0', marginBottom: 12 }}>
            <img src={images[selectedImg]} alt={product.name} style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover' }} onError={e => { e.target.src = 'https://via.placeholder.com/500x600?text=No+Image'; }} />
            {discountPct > 0 && (
              <span style={{ position: 'absolute', top: 16, left: 16, background: '#ef4444', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                {discountPct}% OFF
              </span>
            )}
            {images.length > 1 && (
              <>
                <button onClick={() => setSelectedImg(i => (i - 1 + images.length) % images.length)} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'white', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <ChevronLeft size={18} />
                </button>
                <button onClick={() => setSelectedImg(i => (i + 1) % images.length)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'white', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <ChevronRight size={18} />
                </button>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
            {images.map((img, i) => (
              <img key={i} src={img} alt="" onClick={() => setSelectedImg(i)} style={{ width: 72, height: 84, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: i === selectedImg ? '2px solid #f43397' : '2px solid transparent', opacity: i === selectedImg ? 1 : 0.7, flexShrink: 0 }} />
            ))}
          </div>
        </div>

        {/* Product info */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {product.brand && <p style={{ fontSize: 13, color: '#f43397', fontWeight: 600, marginBottom: 4 }}>{product.brand}</p>}
              <h1 style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3, marginBottom: 12 }}>{product.name}</h1>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleWishlist} style={{ width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'white' }}>
                <Heart size={18} fill={wishlisted ? '#f43397' : 'none'} color={wishlisted ? '#f43397' : '#9ca3af'} />
              </button>
              <button style={{ width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'white' }}>
                <Share2 size={18} color="#9ca3af" />
              </button>
            </div>
          </div>

          {/* Rating */}
          {product.rating > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ background: '#22c55e', color: 'white', padding: '4px 10px', borderRadius: 6, fontWeight: 700, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {Number(product.rating).toFixed(1)} <Star size={12} fill="white" color="white" />
              </span>
              <span style={{ color: '#9ca3af', fontSize: 14 }}>{product.review_count} ratings</span>
            </div>
          )}

          {/* Price */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              <span style={{ fontSize: 32, fontWeight: 800 }}>₹{Number(finalPrice).toLocaleString()}</span>
              {product.original_price > product.price && (
                <span style={{ fontSize: 18, color: '#9ca3af', textDecoration: 'line-through' }}>₹{Number(product.original_price).toLocaleString()}</span>
              )}
            </div>
            {discountPct > 0 && (
              <span style={{ color: '#22c55e', fontWeight: 600, fontSize: 15 }}>{discountPct}% off • You save ₹{(product.original_price - product.price).toLocaleString()}</span>
            )}
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Inclusive of all taxes</p>
          </div>

          {/* Variants */}
          {product.variants?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {['size', 'color'].map(type => {
                const vals = [...new Set(product.variants.filter(v => v[type]).map(v => v[type]))];
                if (!vals.length) return null;
                return (
                  <div key={type} style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, textTransform: 'capitalize' }}>Select {type}:</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {vals.map(val => (
                        <button key={val} onClick={() => setSelectedVariant(product.variants.find(v => v[type] === val))}
                          style={{
                            padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                            border: '1.5px solid', borderColor: selectedVariant?.[type] === val ? '#f43397' : '#e5e7eb',
                            background: selectedVariant?.[type] === val ? '#fce4f1' : 'white',
                            color: selectedVariant?.[type] === val ? '#f43397' : '#374151', fontWeight: 500
                          }}>
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA Button */}
          <div style={{ marginBottom: 24 }}>
            <button onClick={handleBuy} disabled={!product.external_url}
              className="btn btn-primary btn-lg" style={{ width: '100%', opacity: product.external_url ? 1 : 0.6, cursor: product.external_url ? 'pointer' : 'not-allowed' }}>
              <ExternalLink size={18} /> {product.external_url ? `Buy on ${marketplace}` : 'Link coming soon'}
            </button>
          </div>

          {/* Trust badges */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { icon: <Truck size={16} />, text: 'Free Delivery', sub: 'above ₹499' },
              { icon: <RefreshCw size={16} />, text: '7 Day Return', sub: 'Easy returns' },
              { icon: <Shield size={16} />, text: '100% Genuine', sub: 'Authenticated' },
            ].map(b => (
              <div key={b.text} style={{ textAlign: 'center', padding: '12px', background: '#f9f9f9', borderRadius: 10 }}>
                <div style={{ color: '#f43397', display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{b.icon}</div>
                <p style={{ fontSize: 12, fontWeight: 600 }}>{b.text}</p>
                <p style={{ fontSize: 11, color: '#9ca3af' }}>{b.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card" style={{ marginBottom: 40 }}>
        <div className="tabs" style={{ padding: '0 1.5rem' }}>
          {['description', 'reviews', 'specifications'].map(tab => (
            <button key={tab} className={`tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}
              style={{ textTransform: 'capitalize' }}>
              {tab} {tab === 'reviews' && `(${reviews.length})`}
            </button>
          ))}
        </div>
        <div style={{ padding: '1.5rem' }}>
          {activeTab === 'description' && (
            <p style={{ lineHeight: 1.8, color: '#374151' }}>{product.description || 'No description available.'}</p>
          )}
          {activeTab === 'reviews' && (
            <div>
              {reviews.length === 0 ? (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem' }}>No reviews yet. Be the first to review!</p>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 40, marginBottom: 24 }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: 48, fontWeight: 800 }}>{Number(product.rating).toFixed(1)}</p>
                      <div className="stars">{Array.from({length: 5}).map((_, i) => <Star key={i} size={16} fill={i < product.rating ? '#f59e0b' : 'none'} color="#f59e0b" />)}</div>
                      <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>{product.review_count} ratings</p>
                    </div>
                    <div style={{ flex: 1 }}>
                      {ratingDistribution.map(r => (
                        <div key={r.star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, width: 16 }}>{r.star}</span>
                          <Star size={12} fill="#f59e0b" color="#f59e0b" />
                          <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${r.pct}%`, height: '100%', background: '#f59e0b', borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: 13, color: '#9ca3af', width: 20 }}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {reviews.map(rv => (
                    <div key={rv.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 16, marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f43397', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>
                            {rv.user_name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p style={{ fontWeight: 600, fontSize: 14 }}>{rv.user_name}</p>
                            {rv.is_verified && <span style={{ fontSize: 11, color: '#22c55e' }}>✓ Verified Purchase</span>}
                          </div>
                        </div>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>{new Date(rv.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 2, marginBottom: 6 }}>
                        {Array.from({length: 5}).map((_, i) => <Star key={i} size={14} fill={i < rv.rating ? '#f59e0b' : 'none'} color="#f59e0b" />)}
                      </div>
                      {rv.title && <p style={{ fontWeight: 600, marginBottom: 4 }}>{rv.title}</p>}
                      <p style={{ fontSize: 14, color: '#374151' }}>{rv.comment}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
          {activeTab === 'specifications' && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {Object.entries(product.specifications || {}).map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ padding: '10px', fontWeight: 500, color: '#9ca3af', width: '30%', borderBottom: '1px solid #f0f0f0' }}>{k}</td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{v}</td>
                  </tr>
                ))}
                {Object.keys(product.specifications || {}).length === 0 && (
                  <tr><td colSpan={2} style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>No specifications available</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Related Products */}
      {related?.length > 0 && (
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Related Products</h2>
          <div className="product-grid">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
